import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Leaf, ArrowUpRight } from 'lucide-react';
import { fetchAllProducts } from '../api/catalog';
import {
  buildCategoryFilters,
  mapApiProductToCard,
} from '../utils/catalogDisplay';

function ProductsGrid() {
  const [filter, setFilter] = useState('All');
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState(['All']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadProducts() {
      setLoading(true);
      setError('');

      try {
        const productRows = await fetchAllProducts();

        if (cancelled) return;

        setFilters(buildCategoryFilters());
        setProducts(productRows.map(mapApiProductToCard));
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Unable to load products.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadProducts();

    return () => {
      cancelled = true;
    };
  }, []);

  const visibleProducts = useMemo(() => {
    const filtered = filter === 'All'
      ? products
      : products.filter((product) => product.filter === filter);

    return filtered.slice(0, 6);
  }, [filter, products]);

  return (
    <section className="products" id="products">
      <div className="products-glow products-glow-t" />
      <div className="products-glow products-glow-b" />

      <div className="products-inner">
        <div className="reveal products-header">
          <span className="section-eyebrow">— UK Wholesale Range</span>
          <h2 className="section-heading centered">Indian staples your UK customers already love</h2>
          <p className="section-body centered">
            Rice, spices, pulses, flours, oils, and more — export graded from India
            and ready for your wholesale or retail shelves across the United Kingdom.
          </p>
        </div>

        <div className="reveal filter-wrap">
          <div className="filter-pills">
            {filters.map((f) => (
              <button
                key={f}
                type="button"
                className={`filter-pill ${filter === f ? 'active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <p className="product-status reveal">Loading products…</p>
        )}

        {!loading && error && (
          <p className="product-status product-status--error reveal">{error}</p>
        )}

        {!loading && !error && visibleProducts.length === 0 && (
          <p className="product-status reveal">No products available yet.</p>
        )}

        <div className="products-grid">
          {visibleProducts.map((product) => (
            <Link
              key={product.id}
              to={`/products/${product.slug}`}
              className="reveal product-card hover-lift product-card-link"
              style={{ '--p-accent': `var(--${product.accent})` }}
            >
              <div className="product-img">
                <img src={product.image} alt={product.name} loading="lazy" />
                <div className="product-img-overlay" />
                <div className="product-cat-badge">
                  <Leaf size={12} />
                  {product.category}
                </div>
                <div className="product-arrow">
                  <ArrowUpRight size={20} />
                </div>
                <div className="product-img-caption">
                  <p>{product.origin}</p>
                  <h3>{product.name}</h3>
                </div>
              </div>
              <div className="product-body">
                <p className="product-tagline">&ldquo;{product.tagline}&rdquo;</p>
                <p className="product-tasting">
                  <strong>HSN Code:</strong> {product.tasting}
                </p>
              </div>
              <div className="product-accent-bar" />
            </Link>
          ))}
        </div>

        <div className="reveal products-footer">
          <Link to="/products" className="catalog-link">
            View Full UK Export Catalogue
            <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default ProductsGrid;
